import React, { FC, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import OnboardingShell from "./OnboardingShell";
import DocumentDropzone, { MAX_SIZE_MB } from "./DocumentDropzone";
import { Dictionary } from "./i18n/strings";
import { useLocale } from "./i18n/locale";

export interface TenantInsurancePageProps {
  className?: string;
  /** 4 for Flow A (short onboarding), 7 for Flow B (full onboarding). */
  totalSteps?: number;
}

const getAssurances = (t: Dictionary) => [
  // The mockup uses a shield-check for both of the first two.
  {
    title: t.insurance.requiredTitle,
    caption: t.insurance.requiredCaption,
    icon: (
      <>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M9 11.5l2 2 4-4" />
      </>
    ),
  },
  {
    title: t.insurance.coversTitle,
    caption: t.insurance.coversCaption,
    icon: (
      <>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M9 11.5l2 2 4-4" />
      </>
    ),
  },
  {
    title: t.insurance.secureTitle,
    caption: t.insurance.secureCaption,
    icon: (
      <>
        <rect x="4.5" y="10" width="15" height="11" rx="2.5" />
        <path d="M8 10V7.5a4 4 0 018 0V10" />
        <circle cx="12" cy="14.8" r="1.3" />
        <path d="M12 16.1v2.4" />
      </>
    ),
  },
];

const InsuranceIcon = () => (
  <svg
    className="h-12 w-12 text-navy-800"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.3}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M17 18a4 4 0 000-8 6 6 0 00-11.3-2A4.5 4.5 0 006 18" />
    <path d="M12 21v-8m0 0l-3 3m3-3l3 3" />
  </svg>
);

const TenantInsurancePage: FC<TenantInsurancePageProps> = ({
  className = "",
  totalSteps = 7,
}) => {
  const navigate = useNavigate();
  const { t } = useLocale();
  const [file, setFile] = useState<File | null>(null);
  const assurances = getAssurances(t);

  const handleSubmit = () => {
    if (!file) return;
    // TODO: upload the certificate to the DMS before advancing.
    navigate("/tenant/welcome-home");
  };

  return (
    <div
      className={`nc-TenantInsurancePage ${className}`}
      data-nc-id="TenantInsurancePage"
    >
      <Helmet>
        <title>{t.insurance.documentTitle}</title>
      </Helmet>

      <OnboardingShell
        step={6}
        totalSteps={totalSteps}
        title={t.insurance.title}
        subtitle={t.insurance.subtitle}
        footnote={
          <>
            <svg
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.7}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <path d="M9 12l2 2 4-4" />
            </svg>
            {t.insurance.footnote}
          </>
        }
      >
        {/* Plain columns split by hairline dividers — the mockup has no cards
            here. */}
        <ul className="grid grid-cols-3">
          {assurances.map(({ title, caption, icon }, i) => (
            <li
              key={title}
              className={`flex flex-col items-center px-2 text-center ${
                i > 0 ? "border-l border-neutral-200" : ""
              }`}
            >
              <svg
                className="h-7 w-7 text-primary-6000"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                {icon}
              </svg>
              <span className="mt-2 text-[12px] font-semibold leading-tight text-navy-900">
                {title}
              </span>
              <span className="mt-1 px-2 text-[10px] font-medium leading-tight text-neutral-500">
                {caption}
              </span>
            </li>
          ))}
        </ul>

        <div className="mt-5 rounded-2xl bg-cream-100 px-5 py-3">
          <p className="text-[13px] font-semibold text-navy-900">
            {t.insurance.certificate}
          </p>
          <p className="mt-0.5 text-xs text-neutral-500 font-medium">
            {t.insurance.formats(MAX_SIZE_MB)}
          </p>

          <DocumentDropzone
            id="insurance-certificate"
            className="mt-2"
            icon={<InsuranceIcon />}
            prompt={
              <>
                {t.insurance.promptLine1}
                <br />
                {t.insurance.promptLine2}
              </>
            }
            hint={null}
            file={file}
            onFileChange={setFile}
          />
        </div>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={!file}
          className="mt-4 flex w-full items-center justify-between gap-3 rounded-md bg-navy-800 px-6 py-4 text-sm font-medium tracking-wide text-white transition-colors hover:bg-navy-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-800 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <span className="flex-1 text-center">{t.insurance.cta}</span>
          <svg
            className="h-5 w-5 flex-none"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M5 12h18m0 0l-7-7m7 7l-7 7" />
          </svg>
        </button>
      </OnboardingShell>
    </div>
  );
};

export default TenantInsurancePage;
